import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import type { BillingCustomerEmailResponse } from "@/types/api";
import { Badge } from "@/components/ui/badge";

/**
 * "Billing email" row of the subscription card: the address Stripe sends SEPA
 * pre-debit notices, receipts and invoices to. Marked when it is our fallback
 * address (STRIPE_FALLBACK_BILLING_EMAIL) — then the client gets none of them.
 * Looked up live from Stripe, so it only renders once a customer exists.
 */
export function StripeCustomerEmailKV({ name }: { name: string }) {
  const { t } = useTranslation("billing");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["instance", name, "billing", "customer"],
    queryFn: () =>
      api.get<BillingCustomerEmailResponse>(`/api/admin/instances/${name}/billing/customer`),
    retry: false,
  });

  let value: ReactNode;
  if (isLoading) value = <span className="text-muted-foreground">{t("ops.customerEmail.loading")}</span>;
  else if (isError || !data) value = <span className="text-muted-foreground">{t("ops.customerEmail.loadFailed")}</span>;
  else if (data.deleted) value = <span className="text-muted-foreground">{t("ops.customerEmail.deleted")}</span>;
  else {
    value = (
      <span className="flex flex-wrap items-center gap-2">
        <span>{data.email ?? "—"}</span>
        {data.isFallback ? (
          <Badge variant="destructive">
            {t("ops.customerEmail.fallback")}
          </Badge>
        ) : null}
      </span>
    );
  }

  return (
    <div className="flex gap-2">
      <dt className="w-28 text-muted-foreground">{t("ops.customerEmail.label")}</dt>
      <dd className="flex-1 break-all">
        {value}
        {data?.isFallback ? (
          <p className="mt-1 text-xs text-muted-foreground">{t("ops.customerEmail.fallbackHint")}</p>
        ) : null}
      </dd>
    </div>
  );
}
